// src/views/adminView/contractsView/CreateContractWizard.jsx
// Wizard de creación de contrato — rediseñado sobre los primitivos canónicos
// (Button, Input, Select, Modal) + Tailwind/tokens. Reemplaza la versión legacy
// hecha con reactstrap + react-icons + SCSS propio. La capa de datos (estado,
// validaciones async de RUT/email, payloads de empresa/contrato/integraciones/
// usuario, flujos empresa-nueva y holding/Sendu) se conserva 1:1.
import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Copy,
  Building2,
  Briefcase,
  MapPin,
  Users2,
  FileText,
  UserCog,
  Link2,
} from "lucide-react";
import { createCompany, checkCompanyExists } from "../../../api/company";
import { createContract, getRegions, getCommunes } from "../../../api/contracts";
import { useCountry } from "../../../context/CountryContext";
import { createCompanyIntegrations } from "../../../api/integrations";
import { createCompanyUser, checkUserExists } from "../../../api/user";
import { getHoldings } from "../../../api/inform";
import { createHoldingChildCompany } from "../../../api/operatorHoldings";
import { childErrorText } from "./holdings/holdingChildForm";
import { toast } from "../../../utils/toast";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils/cn";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Modal } from "../../../components/ui/Modal";
import { RUT_STATE, rutErrorKey, validateRut } from "../../../utils/rut";
import { isValidTaxId } from "../settings/sections/invoicing/fiscalValidation";
import { TAX_ID_LABEL } from "../settings/sections/invoicing/fiscalCopy";

const WORKING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ADDRESS_TYPES = ["BODEGA", "OFICINA", "OTRO"];

// Campo etiquetado (label + control + error/hint) consistente con la vista de
// detalle de contratos.
function Field({ label, htmlFor, error, hint, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

// Tarjeta de sección dentro de un paso (sin anidar cards: borde simple).
function StepCard({ title, action, children }) {
  return (
    <div className="rounded-lg border border-border bg-bg p-4 sm:p-5 space-y-4">
      {(title || action) && (
        <div className="flex items-center justify-between gap-2">
          {title && <h3 className="text-sm font-semibold text-fg">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// Claves de `errors` que consume AddressBlock. Espejo de las que arma
// validateStep (case 2): el flujo holding las prefija con `holding`.
const ADDRESS_ERROR_KEYS = {
  new: {
    address: "address",
    state: "state",
    municipality: "municipality",
    contactPhone: "contactPhone",
  },
  holding: {
    address: "holdingAddress",
    state: "holdingState",
    municipality: "holdingMunicipality",
    contactPhone: "holdingContactPhone",
  },
};

// ids de los avisos inline (aria-describedby). Uno distinto por campo.
const RUT_ERROR_ID = {
  companyTaxId: "contract-wizard-company-tax-id-error",
  holdingTaxId: "contract-wizard-holding-tax-id-error",
  billingRut: "contract-wizard-billing-rut-error",
  holdingBillingRut: "contract-wizard-holding-billing-rut-error",
  userRut: "contract-wizard-user-rut-error",
};

// Mensaje (ya traducido) del RUT módulo 11: mismo texto que el aviso inline, para
// los errores que arma validateStep al intentar avanzar.
function rutErrorText(t, value) {
  const check = validateRut(value);
  return rutErrorKey(check) ? t(rutErrorKey(check), { dv: check.expectedDv }) : null;
}

// Mensaje del identificador tributario dependiente del país (CL=RUT, AR=CUIT).
// isValidTaxId no distingue "se está escribiendo" de "DV equivocado", así que el
// aviso inline se muestra al salir del campo; al avanzar lo bloquea validateStep.
function taxIdErrorText(t, countryCode) {
  return t("{{taxIdLabel}} inválido (dígito verificador).", {
    taxIdLabel: TAX_ID_LABEL[countryCode] ?? TAX_ID_LABEL.CL,
  });
}

// Campo RUT chileno con aviso inline (misma regla que AcceptDocumentModal: el DV
// equivocado se avisa al tiro, "incompleto" solo tras salir del campo).
function RutField({ id, label, value, onChange, onBlur, touched, parentError, t }) {
  const check = validateRut(value);
  const liveKey =
    check.state === RUT_STATE.BAD_DV || (touched && check.state === RUT_STATE.INCOMPLETE)
      ? rutErrorKey(check)
      : null;
  const error = check.ok ? null : liveKey ? t(liveKey, { dv: check.expectedDv }) : parentError;
  return (
    <Field label={label}>
      <Input
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        invalid={!!error}
        aria-invalid={!!error}
        aria-describedby={error ? id : undefined}
      />
      {error && (
        <p id={id} className="text-xs text-status-error mt-1" role="alert">
          {error}
        </p>
      )}
    </Field>
  );
}

// Campo del identificador tributario del país (RUT en CL, CUIT en AR). A diferencia
// de RutField, isValidTaxId no distingue "se está escribiendo" de "DV equivocado",
// así que el aviso aparece al salir del campo o cuando validateStep bloquea el avance.
function TaxIdField({ id, label, countryCode, value, onChange, onBlur, touched, parentError, hint, disabled, placeholder, t }) {
  const live =
    touched && value.trim() && !isValidTaxId(countryCode, value) ? taxIdErrorText(t, countryCode) : null;
  const error = parentError || live;
  return (
    <Field label={label} hint={error ? undefined : hint}>
      <Input
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        invalid={!!error}
        aria-invalid={!!error}
        aria-describedby={error ? id : undefined}
        disabled={disabled}
        placeholder={placeholder}
      />
      {error && (
        <p id={id} className="text-xs text-status-error mt-1" role="alert">
          {error}
        </p>
      )}
    </Field>
  );
}

// Stepper, AddressBlock y ContactCard viven a nivel de módulo A PROPÓSITO.
// Declarados dentro de CreateContractWizard cambiaban de identidad en cada
// render: React no los reconcilia, desmonta el subárbol y monta uno nuevo, y
// el <input> del DOM se destruye — el foco se perdía en cada tecla.

function Stepper({ steps, currentStep, t }) {
  return (
    <ol className="flex items-center gap-1 overflow-x-auto pb-1" aria-label={t("Progreso")}>
      {steps.map((s, i) => {
        const Icon = s.icon;
        const state = i < currentStep ? "done" : i === currentStep ? "current" : "todo";
        return (
          <li key={s.label} className="flex items-center gap-1 shrink-0">
            <div
              className={cn(
                "flex items-center gap-2 rounded-full px-2.5 py-1.5 text-sm font-medium transition-colors",
                state === "current" && "bg-brand-50 text-brand-700",
                state === "done" && "text-status-success",
                state === "todo" && "text-fg-muted",
              )}
              aria-current={state === "current" ? "step" : undefined}
            >
              <span
                className={cn(
                  "flex items-center justify-center w-6 h-6 rounded-full border text-xs shrink-0",
                  state === "current" && "border-brand-500 bg-brand-500 text-white",
                  state === "done" && "border-status-success bg-status-success-soft text-status-success",
                  state === "todo" && "border-border text-fg-muted",
                )}
              >
                {state === "done" ? <Check className="w-3.5 h-3.5" /> : Icon ? <Icon className="w-3.5 h-3.5" /> : i + 1}
              </span>
              <span className="hidden sm:inline whitespace-nowrap">{s.label}</span>
            </div>
            {i < steps.length - 1 && <span className="w-4 h-px bg-border shrink-0" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

// Bloque de dirección reutilizable por los dos flujos (empresa nueva y
// holding). `prefix` selecciona el juego de claves de error; `communeOptions`
// son las comunas ya resueltas para ESTA dirección.
function AddressBlock({
  address,
  index,
  onUpdate,
  onRemove,
  canRemove,
  prefix,
  regions,
  communeOptions,
  errors,
  t,
}) {
  const errorKeys = ADDRESS_ERROR_KEYS[prefix];
  return (
    <StepCard
      title={`${t("Dirección")} ${index + 1}`}
      action={
        canRemove ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-status-error hover:bg-status-error-soft"
            onClick={() => onRemove(index)}
            iconLeft={<Trash2 className="w-4 h-4" />}
          >
            {t("Quitar")}
          </Button>
        ) : null
      }
    >
      <Field label={`${t("Dirección")} *`} error={errors[`${errorKeys.address}_${index}`]}>
        <Input
          value={address.address}
          onChange={(e) => onUpdate(index, "address", e.target.value)}
          invalid={!!errors[`${errorKeys.address}_${index}`]}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Select
          label={`${t("Región")} *`}
          value={address.state}
          onChange={(e) => onUpdate(index, "state", e.target.value)}
          error={errors[`${errorKeys.state}_${index}`] || undefined}
        >
          <option value="">{t("Seleccione una región")}</option>
          {regions.map((region) => (
            <option key={region.value} value={region.value}>{region.label}</option>
          ))}
        </Select>
        <Select
          label={`${t("Comuna")} *`}
          value={address.municipality}
          onChange={(e) => onUpdate(index, "municipality", e.target.value)}
          disabled={!address.state}
          error={errors[`${errorKeys.municipality}_${index}`] || undefined}
        >
          <option value="">{t("Seleccione una comuna")}</option>
          {communeOptions.map((commune) => (
            <option key={commune.value} value={commune.value}>{commune.label}</option>
          ))}
        </Select>
        <Select label={t("Tipo")} value={address.type} onChange={(e) => onUpdate(index, "type", e.target.value)}>
          {ADDRESS_TYPES.map((tp) => (
            <option key={tp} value={tp}>{tp}</option>
          ))}
        </Select>
      </div>
      <Field
        label={`${t("Teléfono de Contacto")} *`}
        className="sm:max-w-xs"
        error={errors[`${errorKeys.contactPhone}_${index}`]}
      >
        <Input
          value={address.contactPhone}
          onChange={(e) => onUpdate(index, "contactPhone", e.target.value)}
          invalid={!!errors[`${errorKeys.contactPhone}_${index}`]}
        />
      </Field>
    </StepCard>
  );
}

// Tarjeta de contacto reutilizable (principal / facturación).
function ContactCard({ title, contact, onChange, errs, withRut, rutErrorId, t }) {
  // "incompleto" solo se avisa después de salir del campo; el seguimiento del foco es
  // local de cada tarjeta.
  const [rutTouched, setRutTouched] = useState(false);
  return (
    <StepCard title={title}>
      <Field label={`${t("Nombre")} *`} error={errs.name}>
        <Input value={contact.name} onChange={(e) => onChange("name", e.target.value)} invalid={!!errs.name} />
      </Field>
      {withRut && (
        <RutField
          id={rutErrorId}
          label="RUT"
          value={contact.rut}
          onChange={(e) => onChange("rut", e.target.value)}
          onBlur={() => setRutTouched(true)}
          touched={rutTouched}
          parentError={errs.rut}
          t={t}
        />
      )}
      <Field label={`${t("Email")} *`} error={errs.email}>
        <Input type="email" value={contact.email} onChange={(e) => onChange("email", e.target.value)} invalid={!!errs.email} />
      </Field>
      <Field label={`${t("Teléfono")} *`} error={errs.phone}>
        <Input value={contact.phone} onChange={(e) => onChange("phone", e.target.value)} invalid={!!errs.phone} />
      </Field>
    </StepCard>
  );
}

// `prefill` (OPCIONAL — conversión prospecto→cliente desde el tab Pipeline):
// { companyName, businessIdentifier, contactName, contactEmail, contactPhone }.
// Al abrir con prefill se preselecciona el flujo "new" y se siembran empresa y
// contacto principal. Solo siembra valores: las validaciones (formato RUT,
// checkCompanyExists, etc.) corren igual al navegar. Sin prefill, idéntico a hoy.
const CreateContractWizard = ({ isOpen, toggle, onSuccess, inline = false, prefill }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkingRut, setCheckingRut] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [errors, setErrors] = useState({});
  // Campos RUT que ya perdieron el foco: "incompleto" solo se avisa tras onBlur.
  const [rutTouched, setRutTouched] = useState({});
  const touchRut = (key) => setRutTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  // Tipo de empresa: 'new' o 'holding'
  const [companyType, setCompanyType] = useState(null);

  // Datos para empresas de holding
  const [holdings, setHoldings] = useState([]);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [holdingCompanyData, setHoldingCompanyData] = useState({
    name: "",
    businessIdentifier: "",
    shippingAddresses: [
      { address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" },
    ],
    contacts: {
      primary: { name: "", email: "", phone: "", role: "OPERACIONES" },
      billing: { name: "", email: "", phone: "", rut: "", role: "FACTURACION Y COBRANZA" },
    },
    pickUpTime: "",
  });
  const [senduSenderId, setSenduSenderId] = useState("");

  // IDs constantes
  const proposalId = "66ede663adb0a4095c126ff9";
  const businessIdentifierType = "RUT";

  // Paso 1: Información básica de la empresa
  const [companyData, setCompanyData] = useState({
    name: "",
    legalName: "",
    businessIdentifier: "",
    fiscalAddress: "",
    pickUpTime: "",
    ff: true,
    workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  });

  // Regiones y comunas
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState({});
  // El comercio que se crea opera en el mismo país que el operador que lo da de
  // alta. `operationsCountry` va en MAYÚSCULAS para no cambiar el valor que ya
  // se venía escribiendo para Chile ("CHILE").
  const { countryId, countryCode, countryName, loading: countryLoading } = useCountry();
  const operationsCountryId = countryId;
  const operationsCountry = String(countryName || "").toUpperCase();
  // Código ISO ("CL"/"AR") que consume isValidTaxId para elegir el validador del
  // identificador tributario (RUT módulo 11 en CL, CUIT en AR).
  const operationsCountryCode = String(countryCode || "").toUpperCase();

  // Cargar regiones al montar
  useEffect(() => {
    if (isOpen && !countryLoading && countryId) {
      const fetchRegions = async () => {
        const data = await getRegions({ countryId });
        if (!data.error) {
          setRegions(data.map((r) => ({ value: r.code, label: r.name })));
        }
      };
      fetchRegions();
    }
  }, [isOpen, countryId, countryLoading]);

  // Carga las comunas de una región en el slot `addressIndex`. NO toca el
  // estado de direcciones: cada flujo (empresa nueva / holding) actualiza el
  // suyo. Antes escribía siempre sobre `shippingAddresses`, así que desde el
  // flujo holding no limpiaba la comuna y reventaba en la 2ª dirección.
  const loadCommunes = async (addressIndex, regionCode) => {
    if (!countryId) return;
    const data = await getCommunes({ countryId, regionId: regionCode });
    if (!data.error) {
      setCommunes((prev) => ({
        ...prev,
        [addressIndex]: data.map((c) => ({ value: c.name, label: c.name })),
      }));
    }
  };

  // Paso 2: Direcciones de envío
  const [shippingAddresses, setShippingAddresses] = useState([
    { address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" },
  ]);

  // Paso 3: Contactos
  const [contacts, setContacts] = useState({
    primary: { name: "", email: "", phone: "", role: "OPERACIONES" },
    billing: { name: "", email: "", phone: "", rut: "", role: "FACTURACION Y COBRANZA" },
  });

  // Prefill desde un prospecto aprobado: se aplica UNA vez por apertura (el ref
  // evita re-sembrar si el padre re-renderiza con un objeto prefill nuevo
  // mientras el wizard está abierto, lo que pisaría lo que el usuario editó).
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      prefillAppliedRef.current = false;
      return;
    }
    if (prefillAppliedRef.current || !prefill) return;
    prefillAppliedRef.current = true;
    setCompanyType("new");
    setCompanyData((prev) => ({
      ...prev,
      name: prefill.companyName || prev.name,
      businessIdentifier: prefill.businessIdentifier || prev.businessIdentifier,
    }));
    setContacts((prev) => ({
      ...prev,
      primary: {
        ...prev.primary,
        name: prefill.contactName || prev.primary.name,
        email: prefill.contactEmail || prev.primary.email,
        phone: prefill.contactPhone || prev.primary.phone,
      },
    }));
  }, [isOpen, prefill]);

  // Paso 4: Integraciones (solo se crea el documento, no se muestra)
  const [integrations] = useState([]);

  // Paso 5: Información del contrato
  const [contractData, setContractData] = useState({
    dateSigned: new Date().toISOString().split("T")[0], // HOY
    startDate: "",
    endDate: "",
    status: "active",
    insurancePolicy: {
      formulaType: "percentage",
      value: 0.015,
      details: "1.5% del valor declarado del paquete",
    },
  });

  // Paso 6: Usuario administrador
  const [userData, setUserData] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    rut: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Cargar holdings cuando se abre el wizard en modo holding
  useEffect(() => {
    if (isOpen && companyType === "holding") {
      const fetchHoldings = async () => {
        const data = await getHoldings();
        if (!data.error) {
          setHoldings(data.map((h) => ({ value: h._id, label: h.name, companies: h.companies })));
        }
      };
      fetchHoldings();
    }
  }, [isOpen, companyType]);

  const selectedHoldingData = holdings.find((h) => h.value === selectedHolding);
  const isSendu = !!selectedHoldingData?.label?.toLowerCase().includes("sendu");

  // Total de pasos según el tipo de empresa
  const getTotalSteps = () => {
    if (!companyType) return 1; // Paso 0
    if (companyType === "holding") {
      return isSendu ? 4 : 3; // 0,1,2,(3 si Sendu)
    }
    return 6; // Paso 0 + 5 pasos (1-5)
  };

  const totalSteps = getTotalSteps();

  // Metadatos del stepper (label + icono) según el flujo.
  const stepMeta = () => {
    if (companyType === "holding") {
      const base = [
        { label: t("Tipo"), icon: Briefcase },
        { label: t("Holding"), icon: Building2 },
        { label: t("Datos"), icon: FileText },
      ];
      if (isSendu) base.push({ label: t("Sendu"), icon: Link2 });
      return base;
    }
    if (companyType === "new") {
      return [
        { label: t("Tipo"), icon: Briefcase },
        { label: t("Empresa"), icon: Building2 },
        { label: t("Direcciones"), icon: MapPin },
        { label: t("Contactos"), icon: Users2 },
        { label: t("Contrato"), icon: FileText },
        { label: t("Usuario"), icon: UserCog },
      ];
    }
    return [{ label: t("Tipo"), icon: Briefcase }];
  };

  // Generar contraseña segura
  const generateSecurePassword = () => {
    const length = 16;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setUserData({ ...userData, password, confirmPassword: password });
    setShowPassword(true);
    setShowConfirmPassword(true);
  };

  // Copiar contraseña al portapapeles
  const copyPassword = async () => {
    if (!userData.password) {
      toast.warning(t("No hay contraseña para copiar"));
      return;
    }
    try {
      await navigator.clipboard.writeText(userData.password);
      toast.success(t("Contraseña copiada al portapapeles"));
    } catch (error) {
      console.error("Error al copiar la contraseña:", error);
      toast.error(t("Error al copiar la contraseña"));
    }
  };

  // Validar formato de email
  const validateEmailFormat = (email) => {
    if (!email || !email.trim()) return false;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.trim());
  };

  const validateStep = async (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!companyType) {
          newErrors.companyType = t("Debe seleccionar un tipo de empresa");
        }
        break;

      case 1:
        if (companyType === "holding") {
          if (!selectedHolding) {
            newErrors.holding = t("Debe seleccionar un holding");
          }
        } else {
          if (!companyData.name.trim()) newErrors.name = t("El nombre de la empresa es requerido");
          if (!companyData.legalName.trim()) newErrors.legalName = t("El nombre legal es requerido");
          if (!companyData.businessIdentifier.trim()) {
            newErrors.businessIdentifier = t("El RUT es requerido");
          } else if (!isValidTaxId(operationsCountryCode, companyData.businessIdentifier)) {
            // Identificador tributario del país (RUT en CL, CUIT en AR): el módulo 11
            // reemplaza al chequeo de formato, que sólo miraba la forma.
            newErrors.businessIdentifier = taxIdErrorText(t, operationsCountryCode);
          } else {
            setCheckingRut(true);
            try {
              const checkResult = await checkCompanyExists(companyData.businessIdentifier);
              if (checkResult.error) {
                newErrors.businessIdentifier = t("Error al verificar el RUT. Por favor, intente nuevamente.");
              } else if (checkResult.exists) {
                newErrors.businessIdentifier = t("Este RUT ya está registrado en el sistema.");
              }
            } catch (error) {
              console.error("Error checking RUT:", error);
              newErrors.businessIdentifier = t("Error al verificar el RUT. Por favor, intente nuevamente.");
            } finally {
              setCheckingRut(false);
            }
          }
          if (!companyData.fiscalAddress.trim()) newErrors.fiscalAddress = t("La dirección fiscal es requerida");
          if (!companyData.pickUpTime) newErrors.pickUpTime = t("La hora de retiro es requerida");
        }
        break;

      case 2:
        if (companyType === "holding") {
          if (!holdingCompanyData.name.trim()) newErrors.holdingName = t("El nombre de la empresa es requerido");
          if (!holdingCompanyData.businessIdentifier.trim()) {
            newErrors.holdingBusinessIdentifier = t("El RUT es requerido");
          } else if (!isValidTaxId(operationsCountryCode, holdingCompanyData.businessIdentifier)) {
            newErrors.holdingBusinessIdentifier = taxIdErrorText(t, operationsCountryCode);
          } else {
            setCheckingRut(true);
            try {
              const checkResult = await checkCompanyExists(holdingCompanyData.businessIdentifier);
              if (checkResult.error) {
                newErrors.holdingBusinessIdentifier = t("Error al verificar el RUT. Por favor, intente nuevamente.");
              } else if (checkResult.exists) {
                newErrors.holdingBusinessIdentifier = t("Este RUT ya está registrado en el sistema.");
              }
            } catch (error) {
              console.error("Error checking RUT:", error);
              newErrors.holdingBusinessIdentifier = t("Error al verificar el RUT. Por favor, intente nuevamente.");
            } finally {
              setCheckingRut(false);
            }
          }
          if (!holdingCompanyData.pickUpTime) newErrors.holdingPickUpTime = t("La hora de retiro es requerida");

          holdingCompanyData.shippingAddresses.forEach((addr, index) => {
            if (!addr.address.trim()) newErrors[`holdingAddress_${index}`] = t("La dirección es requerida");
            if (!addr.municipality.trim()) newErrors[`holdingMunicipality_${index}`] = t("La comuna es requerida");
            if (!addr.state.trim()) newErrors[`holdingState_${index}`] = t("La región es requerida");
            if (!addr.contactPhone.trim()) newErrors[`holdingContactPhone_${index}`] = t("El teléfono es requerido");
          });

          if (!holdingCompanyData.contacts.primary.name.trim()) newErrors.holdingPrimaryName = t("El nombre del contacto principal es requerido");
          if (!holdingCompanyData.contacts.primary.email.trim()) newErrors.holdingPrimaryEmail = t("El email del contacto principal es requerido");
          if (!holdingCompanyData.contacts.primary.phone.trim()) newErrors.holdingPrimaryPhone = t("El teléfono del contacto principal es requerido");
          if (!holdingCompanyData.contacts.billing.name.trim()) newErrors.holdingBillingName = t("El nombre del contacto de facturación es requerido");
          if (!holdingCompanyData.contacts.billing.email.trim()) newErrors.holdingBillingEmail = t("El email del contacto de facturación es requerido");
          if (!holdingCompanyData.contacts.billing.phone.trim()) newErrors.holdingBillingPhone = t("El teléfono del contacto de facturación es requerido");
          // RUT de facturación: opcional, pero con contenido debe pasar módulo 11.
          if (holdingCompanyData.contacts.billing.rut.trim() && !validateRut(holdingCompanyData.contacts.billing.rut).ok) {
            newErrors.holdingBillingRut = rutErrorText(t, holdingCompanyData.contacts.billing.rut);
          }
        } else {
          shippingAddresses.forEach((addr, index) => {
            if (!addr.address.trim()) newErrors[`address_${index}`] = t("La dirección es requerida");
            if (!addr.municipality.trim()) newErrors[`municipality_${index}`] = t("La comuna es requerida");
            if (!addr.state.trim()) newErrors[`state_${index}`] = t("La región es requerida");
            if (!addr.contactPhone.trim()) newErrors[`contactPhone_${index}`] = t("El teléfono es requerido");
          });
        }
        break;

      case 3:
        if (companyType === "holding") {
          if (isSendu && !senduSenderId.trim()) {
            newErrors.senduSenderId = t("El sender_id es requerido para empresas Sendu");
          }
        } else {
          if (!contacts.primary.name.trim()) newErrors.primaryName = t("El nombre del contacto principal es requerido");
          if (!contacts.primary.email.trim()) newErrors.primaryEmail = t("El email del contacto principal es requerido");
          if (!contacts.primary.phone.trim()) newErrors.primaryPhone = t("El teléfono del contacto principal es requerido");
          if (!contacts.billing.name.trim()) newErrors.billingName = t("El nombre del contacto de facturación es requerido");
          if (!contacts.billing.email.trim()) newErrors.billingEmail = t("El email del contacto de facturación es requerido");
          if (!contacts.billing.phone.trim()) newErrors.billingPhone = t("El teléfono del contacto de facturación es requerido");
          // RUT de facturación: opcional, pero con contenido debe pasar módulo 11.
          if (contacts.billing.rut.trim() && !validateRut(contacts.billing.rut).ok) {
            newErrors.billingRut = rutErrorText(t, contacts.billing.rut);
          }
        }
        break;

      case 4:
        if (!contractData.dateSigned) newErrors.dateSigned = t("La fecha de firma es requerida");
        if (!contractData.startDate) newErrors.startDate = t("La fecha de inicio es requerida");
        break;

      case 5:
        if (!userData.name.trim()) newErrors.name = t("El nombre es requerido");
        if (!userData.lastName.trim()) newErrors.lastName = t("El apellido es requerido");
        if (!userData.email.trim()) {
          newErrors.email = t("El email es requerido");
        } else {
          const normalizedEmail = userData.email.toLowerCase().trim();
          if (!validateEmailFormat(normalizedEmail)) {
            newErrors.email = t("El formato del email es inválido");
          } else {
            setCheckingEmail(true);
            try {
              const checkResult = await checkUserExists(normalizedEmail, userData.rut || "");
              if (checkResult.error) {
                newErrors.email = t("Error al verificar el email. Por favor, intente nuevamente.");
              } else if (checkResult.emailExists) {
                newErrors.email = t("Este email ya está registrado en el sistema.");
              }
            } catch (error) {
              console.error("Error checking email:", error);
              newErrors.email = t("Error al verificar el email. Por favor, intente nuevamente.");
            } finally {
              setCheckingEmail(false);
            }
          }
        }
        if (!userData.phone.trim()) newErrors.phone = t("El teléfono es requerido");
        if (!userData.rut.trim()) {
          newErrors.rut = t("El RUT es requerido");
        } else if (!validateRut(userData.rut).ok) {
          newErrors.rut = rutErrorText(t, userData.rut);
        }
        if (!userData.password.trim()) newErrors.password = t("La contraseña es requerida");
        if (userData.password !== userData.confirmPassword) newErrors.confirmPassword = t("Las contraseñas no coinciden");
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      if (currentStep === 0 && companyType) {
        setCurrentStep(1);
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const addShippingAddress = () => {
    const newIndex = shippingAddresses.length;
    setShippingAddresses([
      ...shippingAddresses,
      { address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" },
    ]);
    setCommunes((prev) => ({ ...prev, [newIndex]: [] }));
  };

  // Las comunas se guardan por índice de dirección: al eliminar una hay que
  // correr el mapa o las filas siguientes quedan mostrando las comunas de otra
  // región.
  const reindexCommunes = (removedIndex, remainingCount) => {
    setCommunes((prev) => {
      const next = {};
      for (let i = 0; i < remainingCount; i++) {
        next[i] = (i < removedIndex ? prev[i] : prev[i + 1]) || [];
      }
      return next;
    });
  };

  const removeShippingAddress = (index) => {
    if (shippingAddresses.length <= 1) return;
    const updated = shippingAddresses.filter((_, i) => i !== index);
    setShippingAddresses(updated);
    reindexCommunes(index, updated.length);
  };

  // Actualización inmutable: `[...arr]` copia el array pero comparte los
  // objetos con el estado anterior, y mutarlos lo pisa hacia atrás. Al cambiar
  // de región se limpia la comuna (ya no pertenece a la región nueva).
  const updateShippingAddress = (index, field, value) => {
    setShippingAddresses((prev) =>
      prev.map((addr, i) =>
        i === index ? { ...addr, [field]: value, ...(field === "state" ? { municipality: "" } : {}) } : addr,
      ),
    );
    if (field === "state" && value) {
      loadCommunes(index, value);
    }
  };

  const addHoldingShippingAddress = () => {
    const updated = [...holdingCompanyData.shippingAddresses];
    updated.push({ address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" });
    setHoldingCompanyData({ ...holdingCompanyData, shippingAddresses: updated });
  };

  const removeHoldingShippingAddress = (index) => {
    if (holdingCompanyData.shippingAddresses.length <= 1) return;
    const updated = holdingCompanyData.shippingAddresses.filter((_, i) => i !== index);
    setHoldingCompanyData((prev) => ({ ...prev, shippingAddresses: updated }));
    reindexCommunes(index, updated.length);
  };

  const updateHoldingShippingAddress = (index, field, value) => {
    setHoldingCompanyData((prev) => ({
      ...prev,
      shippingAddresses: prev.shippingAddresses.map((addr, i) =>
        i === index ? { ...addr, [field]: value, ...(field === "state" ? { municipality: "" } : {}) } : addr,
      ),
    }));
    if (field === "state" && value) {
      loadCommunes(index, value);
    }
  };

  // Helpers de actualización de contactos (primary/billing) para new y holding.
  const setContactField = (which, field, value) =>
    setContacts((prev) => ({ ...prev, [which]: { ...prev[which], [field]: value } }));
  const setHoldingContactField = (which, field, value) =>
    setHoldingCompanyData((prev) => ({
      ...prev,
      contacts: { ...prev.contacts, [which]: { ...prev.contacts[which], [field]: value } },
    }));

  // Usuario del comercio para una empresa Sendu del holding (la convención de siempre:
  // `<sufijo>@sendu.cl` y contraseña aleatoria). Si falla, la empresa ya existe: se avisa.
  const createSenduUser = async (companyId) => {
    const companyNameParts = holdingCompanyData.name.toLowerCase().split(" ");
    const emailPrefix =
      companyNameParts.length > 1
        ? companyNameParts[companyNameParts.length - 1]
        : companyNameParts[0].replace("sendu", "").trim() || "empresa";
    const userEmail = `${emailPrefix}@sendu.cl`;
    const userRut = holdingCompanyData.businessIdentifier;

    const length = 16;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    const userPayload = {
      name: "Sendu",
      lastName: "Spa",
      email: userEmail,
      phone: holdingCompanyData.contacts.primary.phone || "",
      rut: userRut,
      password,
      companyId,
      active: true,
      newUser: false,
      trash: false,
    };

    const userResponse = await createCompanyUser(userPayload);
    if (userResponse.error) {
      toast.error(userResponse.error?.message || userResponse.error || t("Error al crear el usuario Sendu"));
      console.warn("Error creating Sendu user:", userResponse.error);
    }
  };

  // Modo holding: UNA llamada a contracts crea la empresa, su integración, el contrato del
  // holding replicado (el activo más reciente de sus empresas, con inicio hoy) y el vínculo,
  // en una transacción. Antes eran cinco llamadas desde el navegador y el contrato salía con
  // la tarifa vacía. Cada campo va explícito: el DTO rechaza cualquier campo extra.
  const submitHoldingCompany = async () => {
    const holdingData = holdings.find((h) => h.value === selectedHolding);
    if (!holdingData) {
      toast.error(t("Error: No se pudo obtener la información del holding"));
      return;
    }
    const referenceCompany = holdingData.companies?.[0];
    const { primary, billing } = holdingCompanyData.contacts;
    const contact = (c, withRut) => ({
      name: c.name.trim(),
      email: c.email.trim().toLowerCase(),
      phone: c.phone.trim(),
      ...(c.role ? { role: c.role } : {}),
      ...(withRut && c.rut?.trim() ? { rut: c.rut.trim() } : {}),
    });

    let result;
    try {
      const { data } = await createHoldingChildCompany(selectedHolding, {
        name: holdingCompanyData.name.trim(),
        legalName: holdingCompanyData.name.trim().toUpperCase(),
        businessIdentifier: holdingCompanyData.businessIdentifier.trim(),
        fiscalAddress: referenceCompany?.fiscalAddress || holdingCompanyData.name.trim(),
        operationsCountry,
        pickUpTime: holdingCompanyData.pickUpTime,
        workingDays: referenceCompany?.workingDays || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        ff: referenceCompany?.ff !== undefined ? referenceCompany.ff : true,
        shippingAddresses: holdingCompanyData.shippingAddresses.map((a) => ({
          address: a.address.trim(),
          type: a.type,
          municipality: a.municipality,
          state: a.state,
          contactPhone: a.contactPhone.trim(),
        })),
        contacts: { primary: contact(primary, false), billing: contact(billing, true) },
        ...(isSendu && senduSenderId.trim()
          ? { integration: { integrationName: "Sendu", sender_id: senduSenderId.trim() } }
          : {}),
      });
      result = data;
    } catch (error) {
      toast.error(t(childErrorText(error)));
      return;
    }

    if (isSendu) await createSenduUser(result.companyId);

    toast.success(t("Contrato creado exitosamente"));
    handleClose();
    if (onSuccess) onSuccess();
  };

  const handleSubmit = async () => {
    const lastStep = companyType === "holding" ? (isSendu ? 3 : 2) : 5;
    const isValid = await validateStep(lastStep);
    if (!isValid) return;

    setLoading(true);
    try {
      if (companyType === "holding") {
        await submitHoldingCompany();
        return;
      }

      const logisticCompanyId = user?.logisticCompany?._id || user?.logisticCompanyId;

      if (!logisticCompanyId) {
        toast.error(t("Error: No se pudo obtener la compañía logística del usuario"));
        return;
      }

      const companyPayload = {
        name: companyData.name,
        legalName: companyData.legalName,
        businessIdentifier: companyData.businessIdentifier,
        businessIdentifierType,
        fiscalAddress: companyData.fiscalAddress,
        operationsCountry,
        operationsCountryId,
        pickUpTime: companyData.pickUpTime,
        ff: companyData.ff,
        workingDays: companyData.workingDays,
        shippingAddresses,
        contacts: {
          operationsContacts: [contacts.primary],
          billingContacts: [contacts.billing],
        },
        proposalId,
        logisticCompanyId,
      };
      const companyName = companyData.name;

      const companyResponse = await createCompany(companyPayload);
      if (companyResponse.error) {
        toast.error(companyResponse.error?.message || companyResponse.error || t("Error al crear la empresa"));
        return;
      }

      const companyId = companyResponse?._id || companyResponse?.id;
      if (!companyId) {
        toast.error(t("Error: No se pudo obtener el ID de la empresa creada"));
        return;
      }

      const integrationsResponse = await createCompanyIntegrations({
        companyId,
        companyName,
        integrations,
        statusWoocommerce: [],
      });
      if (integrationsResponse.error) {
        toast.error(integrationsResponse.error?.message || integrationsResponse.error || t("Error al crear las integraciones"));
        console.warn("Error creating integrations:", integrationsResponse.error);
      }

      const contractPayload = {
        companyId,
        proposalId,
        logisticsCompanyId: logisticCompanyId,
        dateSigned: contractData.dateSigned
          ? new Date(contractData.dateSigned).toISOString()
          : new Date().toISOString(),
        validityPeriod: {
          startDate: contractData.startDate ? new Date(contractData.startDate).toISOString() : null,
          endDate: contractData.endDate
            ? new Date(contractData.endDate).toISOString()
            : new Date("2039-12-31T23:59:59.000Z").toISOString(),
        },
        status: contractData.status,
        insurancePolicy: contractData.insurancePolicy,
        pricing: {
          packageSizePricing: [],
          packageWeightPricing: [],
          collectPricing: [],
          zonePricing: [],
          shippingTypePricing: [],
        },
        additionalTerms: "",
      };

      const contractResponse = await createContract(contractPayload);
      if (contractResponse.error) {
        toast.error(contractResponse.error?.message || contractResponse.error || t("Error al crear el contrato"));
        return;
      }

      if (!contractResponse._id && !contractResponse.id) {
        toast.error(t("Error: No se pudo obtener el ID del contrato creado"));
        return;
      }

      const userPayload = {
        name: userData.name,
        lastName: userData.lastName,
        email: userData.email.toLowerCase().trim(),
        phone: userData.phone,
        rut: userData.rut,
        password: userData.password,
        companyId,
        active: true,
        newUser: false,
        trash: false,
      };

      const userResponse = await createCompanyUser(userPayload);
      if (userResponse.error) {
        toast.error(userResponse.error?.message || userResponse.error || t("Error al crear el usuario"));
        return;
      }

      toast.success(t("Contrato creado exitosamente"));
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error creating contract:", error);
      toast.error(t("Error al crear el contrato"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setCompanyType(null);
    setSelectedHolding(null);
    setSenduSenderId("");
    setHoldings([]);
    setHoldingCompanyData({
      name: "",
      businessIdentifier: "",
      shippingAddresses: [
        { address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" },
      ],
      contacts: {
        primary: { name: "", email: "", phone: "", role: "OPERACIONES" },
        billing: { name: "", email: "", phone: "", rut: "", role: "FACTURACION Y COBRANZA" },
      },
      pickUpTime: "",
    });
    setCompanyData({
      name: "",
      legalName: "",
      businessIdentifier: "",
      fiscalAddress: "",
      pickUpTime: "",
      ff: true,
      workingDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    });
    setShippingAddresses([
      { address: "", municipality: "", state: "", contactPhone: "", type: "BODEGA" },
    ]);
    setContacts({
      primary: { name: "", email: "", phone: "", role: "OPERACIONES" },
      billing: { name: "", email: "", phone: "", rut: "", role: "FACTURACION Y COBRANZA" },
    });
    setContractData({
      dateSigned: new Date().toISOString().split("T")[0],
      startDate: "",
      endDate: "",
      status: "active",
      insurancePolicy: { formulaType: "percentage", value: 0.015, details: "1.5% del valor declarado del paquete" },
    });
    setCommunes({});
    setUserData({ name: "", lastName: "", email: "", phone: "", rut: "", password: "", confirmPassword: "" });
    setErrors({});
    toggle();
  };

  // ── Pasos ──────────────────────────────────────────────────────────────────
  const renderStep0 = () => {
    const options = [
      { value: "new", icon: Building2, title: t("Empresa Nueva"), desc: t("Crear una empresa completamente nueva con todos los datos") },
      { value: "holding", icon: Briefcase, title: t("Empresa de un Holding"), desc: t("Crear una empresa que pertenece a un holding existente") },
    ];
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-fg">{t("Seleccionar Tipo de Empresa")}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {options.map((opt) => {
            const Icon = opt.icon;
            const active = companyType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCompanyType(opt.value)}
                aria-pressed={active}
                className={cn(
                  "flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-all",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  active ? "border-brand-500 bg-brand-50/60" : "border-border bg-bg hover:border-brand-300",
                )}
              >
                <span className={cn("flex items-center justify-center w-10 h-10 rounded-lg", active ? "bg-brand-500 text-white" : "bg-bg-muted text-fg-muted")}>
                  <Icon className="w-5 h-5" />
                </span>
                <span className="text-sm font-semibold text-fg">{opt.title}</span>
                <span className="text-xs text-fg-muted">{opt.desc}</span>
              </button>
            );
          })}
        </div>
        {errors.companyType && <p className="text-xs text-status-error">{errors.companyType}</p>}
      </div>
    );
  };

  const renderHoldingStep1 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Seleccionar Holding")}</h2>
      <StepCard>
        <Select
          label="Holding *"
          value={selectedHolding || ""}
          onChange={(e) => setSelectedHolding(e.target.value)}
          error={errors.holding || undefined}
        >
          <option value="">{t("Seleccione un holding")}</option>
          {holdings.map((holding) => (
            <option key={holding.value} value={holding.value}>{holding.label}</option>
          ))}
        </Select>
      </StepCard>
    </div>
  );

  const renderHoldingStep2 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Datos de la Empresa")}</h2>
      <StepCard title={t("Información básica")}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={`${t("Nombre de la Empresa")} *`} error={errors.holdingName}>
            <Input
              value={holdingCompanyData.name}
              onChange={(e) => setHoldingCompanyData({ ...holdingCompanyData, name: e.target.value })}
              invalid={!!errors.holdingName}
            />
          </Field>
          <TaxIdField
            id={RUT_ERROR_ID.holdingTaxId}
            label="RUT *"
            countryCode={operationsCountryCode}
            value={holdingCompanyData.businessIdentifier}
            onChange={(e) => {
              setHoldingCompanyData({ ...holdingCompanyData, businessIdentifier: e.target.value });
              if (errors.holdingBusinessIdentifier) setErrors({ ...errors, holdingBusinessIdentifier: null });
            }}
            onBlur={() => touchRut("holdingTaxId")}
            touched={!!rutTouched.holdingTaxId}
            parentError={errors.holdingBusinessIdentifier}
            hint={checkingRut ? t("Verificando RUT...") : undefined}
            disabled={checkingRut}
            placeholder={t("Ej: 12345678-5 o 1234567-4")}
            t={t}
          />
          <Field label={`${t("Hora de Retiro")} *`} error={errors.holdingPickUpTime} className="sm:max-w-xs">
            <Input
              type="time"
              value={holdingCompanyData.pickUpTime}
              onChange={(e) => setHoldingCompanyData({ ...holdingCompanyData, pickUpTime: e.target.value })}
              invalid={!!errors.holdingPickUpTime}
            />
          </Field>
        </div>
      </StepCard>

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-fg">{t("Direcciones de Envío")}</h3>
        <Button size="sm" onClick={addHoldingShippingAddress} iconLeft={<Plus className="w-4 h-4" />}>
          {t("Agregar Dirección")}
        </Button>
      </div>
      {holdingCompanyData.shippingAddresses.map((address, index) => (
        <AddressBlock
          key={index}
          address={address}
          index={index}
          onUpdate={updateHoldingShippingAddress}
          onRemove={removeHoldingShippingAddress}
          canRemove={holdingCompanyData.shippingAddresses.length > 1}
          prefix="holding"
          regions={regions}
          communeOptions={communes[index] || []}
          errors={errors}
          t={t}
        />
      ))}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ContactCard
          title={t("Contacto Principal")}
          contact={holdingCompanyData.contacts.primary}
          onChange={(f, v) => setHoldingContactField("primary", f, v)}
          errs={{ name: errors.holdingPrimaryName, email: errors.holdingPrimaryEmail, phone: errors.holdingPrimaryPhone }}
          t={t}
        />
        <ContactCard
          title={t("Contacto de Facturación")}
          contact={holdingCompanyData.contacts.billing}
          onChange={(f, v) => setHoldingContactField("billing", f, v)}
          errs={{ name: errors.holdingBillingName, email: errors.holdingBillingEmail, phone: errors.holdingBillingPhone, rut: errors.holdingBillingRut }}
          t={t}
          withRut
          rutErrorId={RUT_ERROR_ID.holdingBillingRut}
        />
      </div>
    </div>
  );

  const renderHoldingStep3 = () => {
    if (!isSendu) return null;
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-fg">{t("Configuración de Integración Sendu")}</h2>
        <StepCard>
          <Field
            label="Sender ID *"
            error={errors.senduSenderId}
            hint={t("El alias será igual al sender_id")}
          >
            <Input
              value={senduSenderId}
              onChange={(e) => setSenduSenderId(e.target.value)}
              invalid={!!errors.senduSenderId}
              placeholder={t("Ingrese el sender_id")}
            />
          </Field>
        </StepCard>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Información Básica de la Empresa")}</h2>
      <StepCard>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={`${t("Nombre de la Empresa")} *`} error={errors.name}>
            <Input value={companyData.name} onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })} invalid={!!errors.name} />
          </Field>
          <Field label={`${t("Razón Social")} *`} error={errors.legalName}>
            <Input value={companyData.legalName} onChange={(e) => setCompanyData({ ...companyData, legalName: e.target.value })} invalid={!!errors.legalName} />
          </Field>
          <TaxIdField
            id={RUT_ERROR_ID.companyTaxId}
            label="RUT *"
            countryCode={operationsCountryCode}
            value={companyData.businessIdentifier}
            onChange={(e) => {
              setCompanyData({ ...companyData, businessIdentifier: e.target.value });
              if (errors.businessIdentifier) setErrors({ ...errors, businessIdentifier: null });
            }}
            onBlur={() => touchRut("companyTaxId")}
            touched={!!rutTouched.companyTaxId}
            parentError={errors.businessIdentifier}
            hint={
              checkingRut
                ? t("Verificando RUT...")
                : companyData.businessIdentifier
                  ? t("Formato: XXXXXXX-J o XXXXXXXX-J (J puede ser número o K)")
                  : undefined
            }
            disabled={checkingRut}
            placeholder={t("Ej: 12345678-5 o 1234567-4")}
            t={t}
          />
          <Field label={`${t("Hora de Retiro")} *`} error={errors.pickUpTime}>
            <Input type="time" value={companyData.pickUpTime} onChange={(e) => setCompanyData({ ...companyData, pickUpTime: e.target.value })} invalid={!!errors.pickUpTime} />
          </Field>
        </div>
        <Field label={`${t("Dirección Fiscal")} *`} error={errors.fiscalAddress}>
          <Input value={companyData.fiscalAddress} onChange={(e) => setCompanyData({ ...companyData, fiscalAddress: e.target.value })} invalid={!!errors.fiscalAddress} />
        </Field>
        <Field label={t("Días que opera el comercio")}>
          <div className="flex flex-wrap gap-2">
            {WORKING_DAYS.map((day) => {
              const checked = companyData.workingDays.includes(day);
              const label =
                day === "Monday" ? t("Lunes")
                  : day === "Tuesday" ? t("Martes")
                    : day === "Wednesday" ? t("Miércoles")
                      : day === "Thursday" ? t("Jueves")
                        : day === "Friday" ? t("Viernes")
                          : day === "Saturday" ? t("Sábado") : t("Domingo");
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={checked}
                  onClick={() =>
                    setCompanyData({
                      ...companyData,
                      workingDays: checked
                        ? companyData.workingDays.filter((d) => d !== day)
                        : [...companyData.workingDays, day],
                    })
                  }
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    checked ? "border-brand-500 bg-brand-50 text-brand-700" : "border-border text-fg-muted hover:text-fg",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Field>
      </StepCard>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-fg">{t("Direcciones de Envío")}</h2>
        <Button size="sm" onClick={addShippingAddress} iconLeft={<Plus className="w-4 h-4" />}>
          {t("Agregar Dirección")}
        </Button>
      </div>
      {shippingAddresses.map((address, index) => (
        <AddressBlock
          key={index}
          address={address}
          index={index}
          onUpdate={updateShippingAddress}
          onRemove={removeShippingAddress}
          canRemove={shippingAddresses.length > 1}
          prefix="new"
          regions={regions}
          communeOptions={communes[index] || []}
          errors={errors}
          t={t}
        />
      ))}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Contactos")}</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ContactCard
          title={t("Contacto Principal")}
          contact={contacts.primary}
          onChange={(f, v) => setContactField("primary", f, v)}
          errs={{ name: errors.primaryName, email: errors.primaryEmail, phone: errors.primaryPhone }}
          t={t}
        />
        <ContactCard
          title={t("Contacto de Facturación")}
          contact={contacts.billing}
          onChange={(f, v) => setContactField("billing", f, v)}
          errs={{ name: errors.billingName, email: errors.billingEmail, phone: errors.billingPhone, rut: errors.billingRut }}
          t={t}
          withRut
          rutErrorId={RUT_ERROR_ID.billingRut}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Información del Contrato")}</h2>
      <StepCard>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label={`${t("Fecha de Firma")} *`} error={errors.dateSigned} hint={t("Siempre la fecha actual")}>
            <Input type="date" value={contractData.dateSigned} disabled invalid={!!errors.dateSigned} />
          </Field>
          <Field label={`${t("Fecha de Inicio")} *`} error={errors.startDate}>
            <Input type="date" value={contractData.startDate} onChange={(e) => setContractData({ ...contractData, startDate: e.target.value })} invalid={!!errors.startDate} />
          </Field>
          <Field label={t("Fecha de Término")} hint={t("Si se deja vacío, 31-dic-2039")}>
            <Input type="date" value={contractData.endDate} onChange={(e) => setContractData({ ...contractData, endDate: e.target.value })} />
          </Field>
        </div>
        <Select
          label={t("Estado")}
          value={contractData.status}
          onChange={(e) => setContractData({ ...contractData, status: e.target.value })}
          className="sm:max-w-xs"
        >
          <option value="active">{t("Activo")}</option>
          <option value="inactive">{t("Inactivo")}</option>
          <option value="pending">{t("Pendiente")}</option>
        </Select>
        <Field label={t("Política de Seguro")}>
          <textarea
            rows={3}
            value={contractData.insurancePolicy.details}
            onChange={(e) => setContractData({ ...contractData, insurancePolicy: { ...contractData.insurancePolicy, details: e.target.value } })}
            className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"
          />
        </Field>
      </StepCard>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-fg">{t("Usuario Administrador")}</h2>
      <StepCard>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={`${t("Nombre")} *`} error={errors.name}>
            <Input value={userData.name} onChange={(e) => setUserData({ ...userData, name: e.target.value })} invalid={!!errors.name} />
          </Field>
          <Field label={`${t("Apellido")} *`} error={errors.lastName}>
            <Input value={userData.lastName} onChange={(e) => setUserData({ ...userData, lastName: e.target.value })} invalid={!!errors.lastName} />
          </Field>
          <Field
            label={`${t("Email")} *`}
            error={errors.email}
            hint={checkingEmail ? t("Verificando email...") : userData.email ? t("El email se guardará en minúscula") : undefined}
          >
            <Input
              type="email"
              value={userData.email}
              onChange={(e) => {
                setUserData({ ...userData, email: e.target.value.toLowerCase() });
                if (errors.email) setErrors({ ...errors, email: null });
              }}
              invalid={!!errors.email}
              disabled={checkingEmail}
              placeholder="ejemplo@correo.com"
            />
          </Field>
          <Field label={`${t("Teléfono")} *`} error={errors.phone}>
            <Input value={userData.phone} onChange={(e) => setUserData({ ...userData, phone: e.target.value })} invalid={!!errors.phone} />
          </Field>
          <RutField
            id={RUT_ERROR_ID.userRut}
            label={`${t("RUT")} *`}
            value={userData.rut}
            onChange={(e) => {
              setUserData({ ...userData, rut: e.target.value });
              if (errors.rut) setErrors({ ...errors, rut: null });
            }}
            onBlur={() => touchRut("userRut")}
            touched={!!rutTouched.userRut}
            parentError={errors.rut}
            t={t}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={`${t("Contraseña")} *`} error={errors.password}>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={userData.password}
                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                invalid={!!errors.password}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          <Field label={`${t("Confirmar Contraseña")} *`} error={errors.confirmPassword}>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                value={userData.confirmPassword}
                onChange={(e) => setUserData({ ...userData, confirmPassword: e.target.value })}
                invalid={!!errors.confirmPassword}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                aria-label={showConfirmPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={generateSecurePassword} iconLeft={<KeyRound className="w-4 h-4" />}>
            {t("Generar Contraseña Segura")}
          </Button>
          {userData.password && (
            <Button variant="ghost" size="sm" onClick={copyPassword} iconLeft={<Copy className="w-4 h-4" />}>
              {t("Copiar Contraseña")}
            </Button>
          )}
        </div>
      </StepCard>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep0();
      case 1:
        return companyType === "holding" ? renderHoldingStep1() : renderStep1();
      case 2:
        return companyType === "holding" ? renderHoldingStep2() : renderStep2();
      case 3:
        return companyType === "holding" ? renderHoldingStep3() : renderStep3();
      case 4:
        return companyType === "holding" ? null : renderStep4();
      case 5:
        return companyType === "holding" ? null : renderStep5();
      default:
        return null;
    }
  };

  const footerActions = (
    <>
      <Button variant="ghost" onClick={handleClose} disabled={loading}>
        {t("Cancelar")}
      </Button>
      {currentStep > 0 && (
        <Button variant="outline" onClick={handlePrevious} disabled={loading} iconLeft={<ChevronLeft className="w-4 h-4" />}>
          {t("Anterior")}
        </Button>
      )}
      {currentStep < totalSteps - 1 ? (
        <Button
          onClick={handleNext}
          loading={checkingRut || checkingEmail}
          disabled={loading || checkingRut || checkingEmail}
          iconRight={!(checkingRut || checkingEmail) && <ChevronRight className="w-4 h-4" />}
        >
          {checkingRut || checkingEmail ? t("Verificando...") : t("Siguiente")}
        </Button>
      ) : (
        <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={loading || checkingEmail}>
          {t("Crear Contrato")}
        </Button>
      )}
    </>
  );

  // Render inline: ocupa el área de contenido de la vista (sin overlay).
  if (inline) {
    return (
      <div className="w-full min-w-0 space-y-5 anim-fade-in-up">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={loading} iconLeft={<ChevronLeft className="w-4 h-4" />}>
            {t("Volver")}
          </Button>
          <div className="h-5 w-px bg-border shrink-0" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-fg truncate">{t("Crear Nuevo Contrato")}</h1>
        </div>

        <div className="rounded-lg border border-border bg-bg-subtle/40 px-3 py-2.5">
          <Stepper steps={stepMeta()} currentStep={currentStep} t={t} />
        </div>

        <div>{renderCurrentStep()}</div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          {footerActions}
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="xl"
      title={t("Crear Nuevo Contrato")}
      footer={footerActions}
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-border bg-bg-subtle/40 px-3 py-2.5">
          <Stepper steps={stepMeta()} currentStep={currentStep} t={t} />
        </div>
        {renderCurrentStep()}
      </div>
    </Modal>
  );
};

export default CreateContractWizard;
